import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { DashboardSkeleton } from '../components/Skeleton';
import Icon from '../components/Icon';
import CopyButton from '../components/CopyButton';
import { EmptySignal } from '../components/illustrations';
import { compact } from '../lib/metrics';
import { formatDateTime } from '../lib/time';

export const money = (n, lang) =>
  n == null ? '—' : '₺' + Number(n).toLocaleString(lang === 'en' ? 'en-US' : 'tr-TR', { maximumFractionDigits: 2 });

// 1x altı ROAS'ta 1 basamak yuvarlama "0x" gösterebilir — küçük değerlerde 2 basamak.
export const fmtRoas = (n, lang) =>
  Number(n).toLocaleString(lang === 'en' ? 'en-US' : 'tr-TR', { maximumFractionDigits: Math.abs(n) < 1 ? 2 : 1 }) + 'x';

const CHANNEL_KEY = { youtube: 'YouTube', banner: 'Banner', discord: 'Discord', other: 'other' };

// --- kayıt grafiği + kampanya işaretleri ---------------------------------
function RegChart({ series, campaigns, lang }) {
  const { t } = useTranslation();
  const W = 720, H = 200, padX = 8, padT = 12, padB = 22;
  const pts = (series || []).map((d, i) => ({ i, d: d.d, n: Number(d.n) || 0 }));
  if (pts.length < 2) {
    return <div style={{ height: H, display: 'grid', placeItems: 'center', color: 'var(--text-3)', fontSize: 13 }}>—</div>;
  }
  const maxV = Math.max(...pts.map((p) => p.n), 1);
  const x = (i) => padX + (i / (pts.length - 1)) * (W - padX * 2);
  const y = (v) => padT + (1 - v / maxV) * (H - padT - padB);
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.n).toFixed(1)}`).join(' ');
  const area = `${line} L ${x(pts.length - 1).toFixed(1)} ${H - padB} L ${x(0).toFixed(1)} ${H - padB} Z`;

  const dayIndex = (iso) => {
    const k = String(iso).slice(0, 10);
    const idx = pts.findIndex((p) => p.d === k);
    if (idx >= 0) return idx;
    // aralık dışıysa uçlara sabitle
    return new Date(k) < new Date(pts[0].d) ? 0 : pts.length - 1;
  };

  const markers = [];
  (campaigns || []).forEach((c, ci) => {
    const s = dayIndex(c.startDate);
    markers.push({ x: x(s), label: c.publisherName, kind: 'start', ci });
    if (c.endDate) markers.push({ x: x(dayIndex(c.endDate)), label: c.publisherName, kind: 'end', ci });
  });

  return (
    <div style={{ position: 'relative', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, display: 'block', overflow: 'visible' }}>
        <defs>
          <linearGradient id="regArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {markers.map((m, i) => (
          <g key={i}>
            <line x1={m.x} y1={padT} x2={m.x} y2={H - padB}
              stroke={m.kind === 'start' ? 'var(--accent)' : 'var(--text-3)'}
              strokeWidth="1" strokeDasharray={m.kind === 'start' ? '0' : '3 3'} opacity="0.55" />
            {m.kind === 'start' && (
              <circle cx={m.x} cy={padT} r="3" fill="var(--accent)" />
            )}
          </g>
        ))}
        <path d={area} fill="url(#regArea)" />
        <path d={line} fill="none" stroke="var(--accent)" strokeWidth="1.8" />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-3)', fontSize: 11, marginTop: 2 }}>
        <span>{formatDateTime(pts[0].d, lang).slice(0, 10)}</span>
        <span>{t('growth.chart_caption')}</span>
        <span>{formatDateTime(pts[pts.length - 1].d, lang).slice(0, 10)}</span>
      </div>
    </div>
  );
}

// Backend normalizePublisherName ile birebir aynı mantık (bkz. attribution.js).
function normalizePublisherName(name) {
  return String(name || '')
    .trim().toLowerCase().replace(/\s+/g, ' ')
    .replace(/[^\p{L}\p{N} ]+/gu, '').trim();
}

// --- kampanya ekle formu -------------------------------------------------
function CampaignForm({ channels, onDone, server, publisherIndex, clickHints }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ channel: 'youtube', publisherName: '', publisherUrl: '', landingUrl: '', spendAmount: '', startDate: today, endDate: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const match = useMemo(() => {
    const key = normalizePublisherName(f.publisherName);
    if (!key || !publisherIndex?.publishers?.length) return null;
    return publisherIndex.publishers.find((p) => normalizePublisherName(p.name) === key) || null;
  }, [f.publisherName, publisherIndex]);

  // Faz 5 — yayıncının KENDİ ölçtüğü tıklama aralığı: hiç kampanyası olmayan
  // (yukarıdaki match'i boş dönen) yayıncılar için de çalışır.
  const clickHint = useMemo(() => {
    const key = normalizePublisherName(f.publisherName);
    if (!key || !clickHints?.length) return null;
    return clickHints.find((h) => normalizePublisherName(h.name) === key) || null;
  }, [f.publisherName, clickHints]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.createCampaign({
        channel: f.channel,
        publisherName: f.publisherName,
        publisherUrl: f.publisherUrl || undefined,
        landingUrl: f.landingUrl,
        spendAmount: Number(f.spendAmount),
        startDate: f.startDate,
        endDate: f.endDate || undefined,
      }, server);
      onDone();
    } catch (e2) {
      setErr(t(`growth.err.${e2.code}`, { defaultValue: e2.message }));
    } finally { setBusy(false); }
  };

  return (
    <form className="card" onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
      <h3 style={{ fontSize: '1rem', margin: 0 }}>{t('growth.new_campaign')}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <label className="fld">
          <span>{t('growth.f_channel')}</span>
          <select value={f.channel} onChange={set('channel')}>
            {channels.map((c) => <option key={c} value={c}>{t(`growth.channel_${c}`, { defaultValue: CHANNEL_KEY[c] || c })}</option>)}
          </select>
        </label>
        <label className="fld">
          <span>{t('growth.f_publisher')}</span>
          <input value={f.publisherName} onChange={set('publisherName')} maxLength={120} required placeholder="Kürşat TV" />
          {match && (
            <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
              {t('growth.publisher_hint', { n: match.customers })}
              {match.roas != null && ` · ${t('growth.publisher_hint_roas', { roas: fmtRoas(match.roas, lang) })}`}
            </span>
          )}
          {!match && clickHint && (
            <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
              {t('growth.click_hint', { min: clickHint.minClicks, max: clickHint.maxClicks })}
            </span>
          )}
        </label>
        <label className="fld">
          <span>{t('growth.f_publisher_url')}</span>
          <input value={f.publisherUrl} onChange={set('publisherUrl')} type="url" placeholder="https://youtube.com/@..." />
        </label>
        <label className="fld">
          <span>{t('growth.f_landing')}</span>
          <input value={f.landingUrl} onChange={set('landingUrl')} type="url" required placeholder="https://sunucum.com" />
        </label>
        <label className="fld">
          <span>{t('growth.f_spend')}</span>
          <input value={f.spendAmount} onChange={set('spendAmount')} type="number" min="0" step="1" required placeholder="15000" />
        </label>
        <label className="fld">
          <span>{t('growth.f_start')}</span>
          <input value={f.startDate} onChange={set('startDate')} type="date" required />
        </label>
        <label className="fld">
          <span>{t('growth.f_end')}</span>
          <input value={f.endDate} onChange={set('endDate')} type="date" />
        </label>
      </div>
      {err && <p style={{ color: 'var(--crit)', fontSize: 13, margin: 0 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-sm" disabled={busy}>{busy ? t('common.loading') : t('growth.create')}</button>
      </div>
    </form>
  );
}

// --- kampanya satırı ---------------------------------------------------
function CampaignRow({ c, lang, onChange }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const s = c.stats || {};
  const ended = c.status === 'ended';

  const act = async (fn) => { setBusy(true); try { await fn(); await onChange(); } finally { setBusy(false); } };

  return (
    <div className="card" style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {c.publisherName}
            <span className="srv-tag">{t(`growth.channel_${c.channel}`, { defaultValue: c.channel })}</span>
            {ended && <span className="srv-tag">{t('growth.ended')}</span>}
            <span className={`growth-conf ${s.confidence}`}>{t(`growth.conf_${s.confidence}`)}</span>
          </div>
          <div style={{ color: 'var(--text-3)', fontSize: 12.5, marginTop: 2 }}>
            {formatDateTime(c.startDate, lang).slice(0, 10)} → {c.endDate ? formatDateTime(c.endDate, lang).slice(0, 10) : t('growth.ongoing')} · {money(c.spendAmount, lang)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <CopyButton text={c.trackingUrl} label={t('growth.copy_link')} />
          {!ended && <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => act(() => api.updateCampaign(c.id, { status: 'ended' }))}>{t('growth.end')}</button>}
          <button className="icon-btn" title={t('growth.delete')} disabled={busy}
            onClick={() => { if (window.confirm(t('growth.delete_confirm'))) act(() => api.deleteCampaign(c.id)); }}>
            <Icon name="trash" size={13} />
          </button>
        </div>
      </div>

      <div className="growth-link">
        <Icon name="link" size={13} />
        <code>{c.trackingUrl}</code>
      </div>

      {s.roas != null && (
        <div className="growth-roas">
          <div>
            <span>{t('growth.s_roas')}</span>
            <strong className={s.roas >= 1 ? 'pos' : 'neg'}>{fmtRoas(s.roas, lang)}</strong>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {t('growth.roas_line', { spend: money(c.spendAmount, lang), rev: money(s.revenue, lang) })}
            {s.revenueConfidence === 'partial' && ` · ${t('growth.roas_partial')}`}
          </div>
        </div>
      )}

      <div className="growth-stats">
        <div><span>{t('growth.s_clicks')}</span><strong>{compact(s.clicks || 0, lang)}</strong></div>
        <div><span>{t('growth.s_est_regs')}</span><strong>{compact(s.estRegistrations || 0, lang)}</strong></div>
        {s.pixelConversions > 0 && <div><span>{t('growth.s_pixel')}</span><strong>{compact(s.pixelConversions, lang)}</strong></div>}
        <div><span>{t('growth.s_cpc')}</span><strong>{money(s.costPerClick, lang)}</strong></div>
        <div><span>{t('growth.s_cpr')}</span><strong>{money(s.costPerRegistration, lang)}</strong></div>
      </div>
    </div>
  );
}

// --- piksel kurulum kartı -------------------------------------------
function PixelSetup({ pixel }) {
  const { t } = useTranslation();
  if (!pixel) return null;
  const tag = `<script src="${pixel.scriptUrl}" async></script>`;
  const call = `vpixel('signup', 'OYUNCU_ADI');`;
  return (
    <div className="card">
      <h3 style={{ fontSize: '1rem', marginBottom: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
        {t('growth.pixel_title')}
        <span className={`growth-conf ${pixel.installed ? 'measured' : 'estimated'}`}>
          {pixel.installed ? t('growth.pixel_ok', { n: pixel.conversions }) : t('growth.pixel_waiting')}
        </span>
      </h3>
      <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginBottom: 12 }}>{t('growth.pixel_desc')}</p>
      <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>{t('growth.pixel_step1')}</div>
      <div className="growth-link"><code>{tag}</code></div>
      <div style={{ display: 'flex', gap: 6, margin: '6px 0 14px' }}><CopyButton text={tag} /></div>
      <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>{t('growth.pixel_step2')}</div>
      <div className="growth-link"><code>{call}</code></div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}><CopyButton text={call} /></div>
      <p style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 10 }}>{t('growth.pixel_server_note')}</p>
    </div>
  );
}

// --- cash paket tablosu ----------------------------------------------
function CashPackages({ initial, server }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(initial.length ? initial : [{ tl: '', cash: '' }]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const upd = (i, k, v) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  const add = () => setRows((r) => [...r, { tl: '', cash: '' }]);
  const rm = (i) => setRows((r) => r.filter((_, j) => j !== i));

  const save = async () => {
    setBusy(true); setSaved(false);
    try {
      const clean = rows.map((r) => ({ tl: Number(r.tl), cash: Number(r.cash) })).filter((r) => r.tl > 0 && r.cash > 0);
      const res = await api.updateGrowthSettings({ cashPackages: clean }, server);
      setRows(res.cashPackages.length ? res.cashPackages : [{ tl: '', cash: '' }]);
      setSaved(true); setTimeout(() => setSaved(false), 1800);
    } finally { setBusy(false); }
  };

  return (
    <div className="card">
      <h3 style={{ fontSize: '1rem', marginBottom: 4 }}>{t('growth.cash_title')}</h3>
      <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginBottom: 12 }}>{t('growth.cash_desc')}</p>
      <div style={{ display: 'grid', gap: 8 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="number" min="0" placeholder="₺" value={r.tl} onChange={(e) => upd(i, 'tl', e.target.value)} style={{ width: 110 }} />
            <Icon name="arrow" size={13} style={{ opacity: 0.4 }} />
            <input type="number" min="0" placeholder="cash" value={r.cash} onChange={(e) => upd(i, 'cash', e.target.value)} style={{ width: 140 }} />
            <button className="icon-btn" onClick={() => rm(i)}><Icon name="x" size={13} /></button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn btn-ghost btn-sm" onClick={add}><Icon name="plus" size={12} /> {t('growth.cash_add')}</button>
        <button className="btn btn-sm" onClick={save} disabled={busy}>{saved ? t('growth.saved') : t('common.save')}</button>
      </div>
    </div>
  );
}

export default function Growth() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const { activeServerKey, hasActiveLicense } = useAuth();
  const [data, setData] = useState(null);
  const [pubIndex, setPubIndex] = useState(null);
  const [clickHints, setClickHints] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const server = activeServerKey || undefined;

  const load = useCallback(() => api.growth(server).then(setData), [server]);
  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);
  useEffect(() => { api.publisherIndex().then(setPubIndex).catch(() => {}); }, []);
  useEffect(() => { api.publisherHints().then((r) => setClickHints(r.hints)).catch(() => {}); }, []);

  const toggleOptIn = async (val) => {
    await api.updateGrowthSettings({ adMeasurementOptIn: val }, server);
    load();
  };

  const totals = data?.totals || {};

  if (loading) return <DashboardLayout title={t('growth.title')} heading={false}><DashboardSkeleton /></DashboardLayout>;

  if (!hasActiveLicense || !data?.hasLicense) {
    return (
      <DashboardLayout title={t('growth.title')} heading={false}>
        <h1 className="page-title">{t('growth.title')}</h1>
        <div className="empty-state">
          <EmptySignal />
          <h3>{t('growth.none_title')}</h3>
          <p>{t('growth.none_desc')}</p>
          <Link to="/checkout" className="btn btn-sm" style={{ marginTop: 'var(--sp-5)' }}>{t('checkout.title')}</Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('growth.title')} heading={false}>
      <h1 className="page-title">{t('growth.title')}</h1>
      <p className="page-sub">{t('growth.subtitle')}</p>

      {/* özet */}
      <div className="growth-summary card">
        <div><span>{t('growth.t_spend')}</span><strong>{money(totals.spend, lang)}</strong></div>
        <div><span>{t('growth.t_clicks')}</span><strong>{compact(totals.clicks || 0, lang)}</strong></div>
        <div><span>{t('growth.t_est_regs')}</span><strong>{compact(totals.estRegistrations || 0, lang)}</strong></div>
        {totals.roas != null ? (
          <>
            <div><span>{t('growth.t_revenue')}</span><strong>{money(totals.revenue, lang)}</strong></div>
            <div>
              <span>{t('growth.t_roas')}</span>
              <strong className={totals.roas >= 1 ? 'pos' : 'neg'}>{fmtRoas(totals.roas, lang)}</strong>
            </div>
          </>
        ) : (
          <div><span>{t('growth.t_cpr')}</span><strong>{money(totals.costPerRegistration, lang)}</strong></div>
        )}
      </div>
      <p className="growth-disclaimer">{t('growth.estimate_note')}</p>

      {/* yayıncı endeksi teaser */}
      <Link to="/dashboard/publishers" className="card card-hover" style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 'var(--sp-4)', textDecoration: 'none', color: 'inherit' }}>
        <Icon name="bar-chart" size={18} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>{t('growth.pubidx_teaser_title')}</div>
          <div style={{ color: 'var(--text-3)', fontSize: 12.5 }}>
            {pubIndex
              ? t('growth.pubidx_teaser_desc', { n: pubIndex.publishers.length })
              : t('common.loading')}
          </div>
        </div>
        <Icon name="arrow" size={14} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
      </Link>

      {/* kayıt grafiği */}
      <div className="card" style={{ marginTop: 'var(--sp-4)' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>{t('growth.reg_chart_title')}</h3>
        <RegChart series={data.registrationSeries} campaigns={data.campaigns} lang={lang} />
      </div>

      {/* kampanyalar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 'var(--sp-5) 0 var(--sp-3)' }}>
        <h2 style={{ fontSize: '1.05rem', margin: 0 }}>{t('growth.campaigns')}</h2>
        <button className="btn btn-sm" onClick={() => setShowForm((v) => !v)}>
          <Icon name={showForm ? 'x' : 'plus'} size={13} /> {showForm ? t('common.cancel') : t('growth.add_campaign')}
        </button>
      </div>

      {showForm && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <CampaignForm channels={data.channels} server={server} publisherIndex={pubIndex} clickHints={clickHints} onDone={() => { setShowForm(false); load(); }} />
        </div>
      )}

      {data.campaigns.length === 0 ? (
        <div className="card" style={{ color: 'var(--text-3)', fontSize: 13 }}>{t('growth.no_campaigns')}</div>
      ) : (
        <div className="list-plain">
          {data.campaigns.map((c) => <CampaignRow key={c.id} c={c} lang={lang} onChange={load} />)}
        </div>
      )}

      {/* piksel — hassas ölçüm */}
      <div style={{ marginTop: 'var(--sp-5)' }}>
        <PixelSetup pixel={data.pixel} />
      </div>

      {/* ayarlar */}
      <div style={{ display: 'grid', gap: 'var(--sp-4)', marginTop: 'var(--sp-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <CashPackages initial={data.cashPackages || []} server={server} />
        <div className="card">
          <h3 style={{ fontSize: '1rem', marginBottom: 4 }}>{t('growth.optin_title')}</h3>
          <p style={{ color: 'var(--text-3)', fontSize: 12.5, marginBottom: 12 }}>{t('growth.optin_desc')}</p>
          <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer', fontSize: 13.5 }}>
            <input type="checkbox" checked={!!data.optIn} onChange={(e) => toggleOptIn(e.target.checked)} />
            {t('growth.optin_label')}
          </label>
          <p style={{ fontSize: 12, marginTop: 10 }}>
            <Link to="/gizlilik-politikasi" style={{ color: 'var(--text-3)' }}>{t('growth.optin_privacy')}</Link>
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
